/**
 * Weitere Kontaktwege und Ansprechpartner eines Kunden (24.09.2026).
 *
 * Beides liegt in `kunde.kontakte_json` (siehe `KontaktZeile` in
 * lib/kundeAnlage.ts). Jede Liste zeigt nur ihre Art, gibt aber immer die
 * ganze Liste zurück — sonst gingen beim Speichern die Einträge der anderen
 * Art verloren.
 *
 * Eigenständig gestylt (styles/kunden.css, `k-kliste`), weil beide Listen in
 * der Kundenmaske und im Anlege-Dialog des Belegeditors stehen.
 */
import type { Anrede } from "../../lib/anrede";
import { leererKontakt, leererKontaktweg, type KontaktZeile, type PersonAnrede } from "../../lib/kundeAnlage";
import type { Kunde } from "../../lib/types";
import AnredeFeld from "./AnredeFeld";
import { MailKnopf, TelefonLink, kundeMailZiele } from "./KontaktAktionen";
import "../../styles/kunden.css";

type Props = {
  kontakte: KontaktZeile[];
  onChange: (kontakte: KontaktZeile[]) => void;
  /**
   * Der gespeicherte Kunde — dann steht an jeder Nummer „Anrufen" und an jeder
   * Adresse „Mail". Ohne ihn (Anlegen) nicht: eine Mail braucht einen Kunden,
   * in dessen Verlauf sie gehört.
   */
  kunde?: Kunde | null;
};

const PERSON_ANREDEN: readonly Anrede[] = ["", "frau", "herr", "du"];
const PERSON_ANREDE_LABEL: Partial<Record<Anrede, string>> = { "": "Neutral", du: "Du" };

function aendern(liste: KontaktZeile[], i: number, patch: Partial<KontaktZeile>): KontaktZeile[] {
  return liste.map((k, j) => (j === i ? { ...k, ...patch } : k));
}

/** Weitere Telefonnummern und Mailadressen des Kunden selbst — Privat wie Firma. */
export function KontaktwegeFeld({ kontakte, onChange, kunde }: Props) {
  const zeilen = kontakte.map((k, i) => ({ k, i })).filter(({ k }) => k.art === "kontakt");
  return (
    <div className="k-kliste">
      <div className="k-kliste-kopf">
        <span>Weitere Telefonnummern und E-Mail-Adressen</span>
        <div className="k-kliste-knoepfe">
          <button type="button" className="k-kliste-plus" onClick={() => onChange([...kontakte, { ...leererKontaktweg(), phone: " " }])}>+ Telefon</button>
          <button type="button" className="k-kliste-plus" onClick={() => onChange([...kontakte, { ...leererKontaktweg(), email: " " }])}>+ E-Mail</button>
        </div>
      </div>
      {zeilen.map(({ k, i }) => {
        const istMail = k.email !== "" || (k.phone === "" && k.email === "");
        return (
          <div className="k-kliste-weg" key={i}>
            <label>
              <span>{istMail ? "E-Mail" : "Telefon"}</span>
              <input
                type={istMail ? "email" : "tel"}
                value={(istMail ? k.email : k.phone).trimStart()}
                // Ein Leerzeichen hält die Art, solange das Feld noch leer ist.
                onChange={(e) => onChange(aendern(kontakte, i, istMail ? { email: e.target.value || " " } : { phone: e.target.value || " " }))}
                autoFocus={(istMail ? k.email : k.phone) === " "}
              />
            </label>
            <label>
              <span>Bezeichnung</span>
              <input value={k.role} placeholder={istMail ? "z. B. privat, Büro" : "z. B. mobil, Büro"} onChange={(e) => onChange(aendern(kontakte, i, { role: e.target.value }))} />
            </label>
            {kunde && (istMail
              ? <MailKnopf email={k.email} kundeId={kunde.id} kundeName={kunde.name} ziele={kundeMailZiele(kunde)} className="k-kliste-tat">Mail</MailKnopf>
              : <TelefonLink nummer={k.phone} className="k-kliste-tat">Anrufen</TelefonLink>)}
            <button type="button" className="k-kliste-weg-x" aria-label="Entfernen" onClick={() => onChange(kontakte.filter((_, j) => j !== i))}>×</button>
          </div>
        );
      })}
    </div>
  );
}

/** Ansprechpartner einer Firma, jeder mit eigener Anrede. */
export function AnsprechpartnerFeld({ kontakte, onChange, kunde }: Props) {
  const zeilen = kontakte.map((k, i) => ({ k, i })).filter(({ k }) => k.art === "person");
  return (
    <div className="k-kliste">
      <div className="k-kliste-kopf">
        <span>Ansprechpartner</span>
        <button type="button" className="k-kliste-plus" onClick={() => onChange([...kontakte, leererKontakt()])}>+ Person</button>
      </div>
      {zeilen.length === 0 && (
        <p className="k-kliste-leer">
          Die Firma selbst wird mit „Sehr geehrte Damen und Herren“ angeschrieben. Geht eine Mail an einen
          Ansprechpartner, bekommt er seine eigene Anrede.
        </p>
      )}
      {zeilen.map(({ k, i }) => (
        <div className="k-kliste-person" key={i}>
          <AnredeFeld
            anrede={k.anrede} vorname={k.vorname} nachname={k.nachname}
            anreden={PERSON_ANREDEN} label={PERSON_ANREDE_LABEL}
            onChange={(w) => onChange(aendern(kontakte, i, { ...w, anrede: w.anrede as PersonAnrede }))}
          />
          <div className="k-kliste-felder">
            <label><span>Rolle</span><input value={k.role} placeholder="z. B. Bauleitung" onChange={(e) => onChange(aendern(kontakte, i, { role: e.target.value }))} /></label>
            <label><span>E-Mail</span><input type="email" value={k.email} onChange={(e) => onChange(aendern(kontakte, i, { email: e.target.value }))} /></label>
            <label><span>Telefon</span><input type="tel" value={k.phone} onChange={(e) => onChange(aendern(kontakte, i, { phone: e.target.value }))} /></label>
          </div>
          <div className="k-kliste-fuss">
            {kunde && <TelefonLink nummer={k.phone} className="k-kliste-tat">Anrufen</TelefonLink>}
            {kunde && <MailKnopf email={k.email} kundeId={kunde.id} kundeName={kunde.name} ziele={kundeMailZiele(kunde)} className="k-kliste-tat">Mail</MailKnopf>}
            <button type="button" className="k-kliste-entfernen" onClick={() => onChange(kontakte.filter((_, j) => j !== i))}>Entfernen</button>
          </div>
        </div>
      ))}
    </div>
  );
}
